import {
  endOfMonth,
  endOfQuarter,
  getMonth,
  getQuarter,
  startOfMonth,
  startOfQuarter,
  subMonths,
  subQuarters,
} from 'date-fns';

export type PeriodType = 'month' | 'quarter' | 'annual';

export type PeriodRangePayload =
  | {
      from: Date;
      to: Date;
    }
  | {
      from: undefined;
      to: undefined;
    };

export type PeriodRange =
  | {
      key: PeriodRangeKey;
    }
  | {
      key: 'custom';
      from: Date;
      to: Date;
    };

export type PeriodRangeKey =
  | 'currentMonth'
  | 'previousMonth'
  | 'currentQuarter'
  | 'previousQuarter'
  | 'overall';

export type PeriodPosition = {
  type: PeriodType;
  /** zero-based index of the period within the year
   * - for 'month': 0 (January) to 11 (December)
   * - for 'quarter': 0 (Q1) to 3 (Q4)
   * - for 'annual': 0
   */
  index: number;
};

export const getPeriodType = (
  periodicity: 'monthly' | 'quarterly' | 'yearly',
): PeriodType => {
  switch (periodicity) {
    case 'monthly':
      return 'month';
    case 'quarterly':
      return 'quarter';
    default:
      return 'annual';
  }
};

export const getPeriodIndex = (
  date: Date,
  periodType: 'annual' | 'month' | 'quarter',
): number => {
  if (periodType === 'month') {
    return getMonth(date);
  }
  if (periodType === 'quarter') {
    return getQuarter(date) - 1;
  }
  return 0;
};

export const toPeriodRangePayload = (
  periodRange: PeriodRange,
  referenceDate: Date = new Date(),
): PeriodRangePayload => {
  if (periodRange.key === 'custom') {
    return {
      from: startOfMonth(periodRange.from),
      to: endOfMonth(periodRange.to),
    };
  }
  switch (periodRange.key) {
    case 'currentMonth':
      return {
        from: startOfMonth(referenceDate),
        to: endOfMonth(referenceDate),
      };
    case 'previousMonth': {
      const previousMonth = subMonths(referenceDate, 1);
      return {
        from: startOfMonth(previousMonth),
        to: endOfMonth(previousMonth),
      };
    }
    case 'currentQuarter':
      return {
        from: startOfQuarter(referenceDate),
        to: endOfQuarter(referenceDate),
      };
    case 'previousQuarter': {
      const previousQuarter = subQuarters(referenceDate, 1);
      return {
        from: startOfQuarter(previousQuarter),
        to: endOfQuarter(previousQuarter),
      };
    }
    case 'overall':
      return {
        from: undefined,
        to: undefined,
      };
    default:
      // fallback to currentMonth
      return {
        from: startOfMonth(referenceDate),
        to: endOfMonth(referenceDate),
      };
  }
};
